/**
 * Shopify Product List — Highlight Block Injector (with Infinite Scroll support)
 *
 * Usage:
 *   initHighlightInjector({ highlights, gridSelector, itemSelector, columns, rowInterval })
 *
 * Works with:
 *   - Static grids (initial page load)
 *   - Infinite scroll (MutationObserver watches for new cards)
 *   - Pagination-based "Load more" buttons
 */

(function () {
    // Bump when logic changes — lets you verify the deployed version via
    // DevTools: look for this log line, or check window._hlInjectorVersion.
    const HL_VERSION = 'v2.1 cell-based / no product-item class';

    /**
     * @typedef {Object} HighlightEntry
     * @property {"Small"|"Large"} grid_span  - "Large" = 1-col full width, "Small" = 2-col split
     * @property {string}          image      - Resolved image URL (handle GID→URL in Liquid)
     * @property {string}          text       - Heading text for the highlight block
     */

    /**
     * @param {Object}           opts
     * @param {HighlightEntry[]} opts.highlights              - Metaobject highlight entries
     * @param {string}           [opts.gridSelector]          - CSS selector for the product grid container
     * @param {string}           [opts.itemSelector]          - CSS selector for each product card
     * @param {number}           [opts.columns=3]             - Products per row (desktop)
     * @param {number}           [opts.mobileColumns=2]       - Products per row at/under mobileBreakpoint
     * @param {number}           [opts.rowInterval=5]         - Insert a highlight block every N product rows
     * @param {Function}         [opts.resolveImageUrl]       - Optional GID → URL resolver
     * @param {number}           [opts.startDelay=0]          - Wait N ms before the first scan (lets other
     *                                                          plugins, e.g. variant-flatteners, finish first)
     */
    function initHighlightInjector(opts) {
        const {
            highlights    = [],
            gridSelector  = '.product-grid',
            itemSelector  = '.grid__item',
            columns       = 3,
            mobileColumns = 2,      // grid columns at/under mobileBreakpoint
            mobileBreakpoint = 640, // px; matches the CSS @media breakpoint
            rowInterval   = 5,
            resolveImageUrl = null,
            startDelay    = 0,
        } = opts;

        if (!highlights.length) return;

        window._hlInjectorVersion = HL_VERSION;
        console.info('[highlight-injector]', HL_VERSION);

        // Random render order, shuffled ONCE per page load (Fisher–Yates).
        // Kept stable across grid re-renders / breakpoint changes so blocks
        // don't visibly swap around after the initial paint.
        const shuffled = [...highlights];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }

        // Split into span pools so rendering ALTERNATES Large → Small →
        // Large → Small… Each pool keeps its shuffled order and cycles
        // independently; if one type doesn't exist, the other is used
        // every time.
        const largePool = shuffled.filter((h) => h.grid_span === 'Large');
        const smallPool = shuffled.filter((h) => h.grid_span !== 'Large');

        injectStyles();

        // Delay the first scan so other plugins (e.g. a variant-flattener that
        // rewrites the product cards) can finish mutating the DOM first.
        if (startDelay > 0) {
            setTimeout(start, startDelay);
        } else {
            start();
        }

        function start() {
            // Single-instance guard: if a previous instance is running (e.g.
            // the theme editor re-ran the section script), disconnect it and
            // remove its blocks so two instances can never stack highlights.
            if (typeof window._hlInjectorDisconnect === 'function') {
                window._hlInjectorDisconnect();
            }
            document.querySelectorAll('[data-hl]').forEach((el) => el.remove());

            const grid = document.querySelector(gridSelector);
            if (!grid) {
                console.warn('[highlight-injector] Grid not found:', gridSelector);
                return;
            }

            // ── State ─────────────────────────────────────────────────────────────────
            // We track every product card we've already accounted for in a Set, then
            // re-scan the live DOM on each mutation. This is idempotent: missed,
            // duplicated, or out-of-order mutations can't desync the count, and it
            // survives the grid node being replaced by the theme.
            //
            // Placement: exactly rowInterval rows' worth of PRODUCTS
            // (rowInterval × columns items = 15 on a 3-column desktop, 10 on
            // a 2-column phone) sit between one highlight's end and the next
            // highlight's start. The grid is counted in cells (products =
            // 1 cell; Large highlight = 2 of 3 desktop columns; 1 of 2 on
            // phone). Because highlights consume cells themselves, targets
            // drift across columns; when a target column can't fit the
            // block, it moves FORWARD to the start of the next row — no
            // empty boxes. With all-Large highlights on desktop this yields
            // rows 6, 12, 18… Rendering alternates Large → Small → Large…
            // and each span pool repeats from its start once exhausted, so
            // highlights keep appearing for as long as products load.
            // Crossing `mobileBreakpoint` resets and re-injects so spacing
            // stays correct on both layouts.
            let seen = new WeakSet();     // product cards already counted
            let countedCards   = [];      // same cards, in the order counted (for desync checks)
            let totalCells     = 0;       // cells consumed so far (products + highlights)
            let insertedBlocks = [];      // highlight elements we've placed in the DOM
            let largeIdx       = 0;       // cycling pointer into `largePool`
            let smallIdx       = 0;       // cycling pointer into `smallPool`
            let wantLarge      = true;    // alternation flag: Large → Small → Large…

            const mql = window.matchMedia(`(max-width: ${mobileBreakpoint}px)`);

            /** Columns for the CURRENT viewport. */
            function currentCols() {
                return Math.max(1, mql.matches ? mobileColumns : columns);
            }

            /** Product cells between one highlight's end and the next's start. */
            function intervalCells() {
                return rowInterval * currentCols();
            }

            let targetCell = intervalCells(); // cell the next highlight should start at

            /**
             * Grid cells a highlight occupies in the CURRENT viewport.
             * Mirrors the theme width classes on .hl-row:
             *   Large = 2/3--desk, Small = 1/3--desk, both 1/2--phone.
             */
            function highlightCells(hl) {
                const cols = currentCols();
                if (mql.matches) return Math.max(1, Math.round(cols / 2)); // 1/2--phone
                const frac = hl.grid_span === 'Large' ? 2 / 3 : 1 / 3;
                return Math.min(cols, Math.max(1, Math.round(cols * frac)));
            }

            /** Reset everything so the next scan re-inserts from scratch. */
            function resetState() {
                insertedBlocks.forEach((el) => { if (el.isConnected) el.remove(); });
                insertedBlocks = [];
                seen           = new WeakSet();
                countedCards   = [];
                totalCells     = 0;
                largeIdx       = 0;
                smallIdx       = 0;
                wantLarge      = true;
                targetCell     = intervalCells();
            }

            /**
             * Re-scan the grid for product cards, process any not yet seen (in
             * DOM order). The next highlight (alternating Large/Small,
             * cycling forever) starts rowInterval rows' worth of products
             * after the previous one, moved forward to the next row start
             * when the target column can't fit the block's width.
             *
             * Safe to call as often as you like; idempotent via `seen`.
             */
            function scanAndInject() {
                // Self-heal: theme scripts that select `.product-item` (e.g.
                // the grid/list layout switcher) rewrite className on our
                // wrappers too, turning highlights into fake product cards.
                // Restore the original classes whenever that happens.
                insertedBlocks.forEach((el) => {
                    if (!el.isConnected) return;
                    const original = el.getAttribute('data-hl-class');
                    if (original && el.className !== original) el.className = original;
                    if (!el.dataset.hl) el.dataset.hl = '1';
                });

                // Wipe detection: theme re-rendered the grid and destroyed our
                // rows -> reset and re-insert all.
                if (insertedBlocks.length && insertedBlocks.some((el) => !el.isConnected)) {
                    resetState();
                }

                const liveGrid = document.querySelector(gridSelector);
                if (!liveGrid) return false;

                const cards = Array.from(liveGrid.querySelectorAll(itemSelector))
                    .filter((c) => !(
                        (c.dataset && c.dataset.hl)          // our stamp
                        || c.classList.contains('hl-row')    // our wrapper
                        || c.querySelector('.hl-block')      // our content (belt & braces
                    ));                                      //  in case attrs get rewritten)

                // Desync detection: a variant-flattener (or any script that
                // inserts, replaces, or removes cards among ones we already
                // counted) shifts every row below the change, breaking our
                // cell math and leaving empty columns. Wipe and re-inject
                // against the new card order when:
                //   a) a card we counted was removed from the DOM, or
                //   b) a NEW (unseen) card appears BEFORE the last card we
                //      counted (i.e., not a simple append).
                if (countedCards.length) {
                    const lastCounted = countedCards[countedCards.length - 1];
                    const desynced =
                        countedCards.some((el) => !el.isConnected)
                        || cards.some((c) => !seen.has(c)
                            && (lastCounted.compareDocumentPosition(c) & Node.DOCUMENT_POSITION_PRECEDING));
                    if (desynced) resetState();
                }

                for (const card of cards) {
                    if (seen.has(card)) continue;
                    seen.add(card);
                    countedCards.push(card);

                    if (totalCells > 0) {
                        // Alternate spans (falling back to the non-empty pool
                        // when one type doesn't exist); each pool cycles from
                        // its start once exhausted.
                        const useLarge = !!(largePool.length && (wantLarge || !smallPool.length));
                        const pool     = useLarge ? largePool : smallPool;
                        const hl       = pool[(useLarge ? largeIdx : smallIdx) % pool.length];
                        const cols     = currentCols();
                        const hlCells  = highlightCells(hl);

                        // Cell the highlight should start at; if that column
                        // can't fit the block, move FORWARD to the start of
                        // the next row.
                        const offset     = targetCell % cols;
                        const insertCell = (offset + hlCells > cols)
                            ? targetCell + (cols - offset)
                            : targetCell;

                        // This card sits at cell `totalCells`; insert the
                        // highlight just before it when we've reached the
                        // target (>= also catches up after a grid wipe).
                        if (totalCells >= insertCell) {
                            const block = buildHighlightRow(hl, resolveImageUrl);
                            card.before(block);
                            insertedBlocks.push(block);
                            totalCells += hlCells;                       // hl shifts the row math
                            targetCell  = totalCells + intervalCells();  // 15 product cells after the hl's end
                            if (useLarge) largeIdx++; else smallIdx++;
                            wantLarge   = !useLarge; // alternate for the next slot
                        }
                    }

                    totalCells += 1; // the product card
                }
                return true;
            }

            // Breakpoint change (rotate / resize): row width changed, so
            // re-place everything with the new interval.
            const onBreakpointChange = () => { resetState(); scheduleScan(); };
            if (mql.addEventListener) mql.addEventListener('change', onBreakpointChange);
            else mql.addListener(onBreakpointChange); // older Safari

            // ── Initial render ──────────────────────────────────────────────────────
            scanAndInject();

            // ── MutationObserver: runs for the LIFETIME of the page ──────────────────
            // We observe a STABLE ancestor (the grid's parent, or body) so that even
            // if the grid node itself gets replaced, we still hear about it. subtree
            // catches cards appended at any depth.
            //
            // The observer must NEVER disconnect on "all highlights placed":
            // infinite scroll can re-render the grid at any time, wiping our
            // rows. The wipe-detection in scanAndInject() re-inserts them, but
            // only if something is still listening.
            const watchRoot = grid.parentElement || document.body;

            // Debounce: a re-render fires dozens of mutations in one burst.
            // One scan per frame after the burst is enough.
            let scanScheduled = false;
            function scheduleScan() {
                if (scanScheduled) return;
                scanScheduled = true;
                requestAnimationFrame(() => {
                    scanScheduled = false;
                    scanAndInject();
                });
            }

            const observer = new MutationObserver(scheduleScan);
            observer.observe(watchRoot, {
                childList: true,
                subtree: true,
                attributes: true,           // hear class rewrites on our blocks
                attributeFilter: ['class'], // (self-heal in scanAndInject)
            });

            // Startup fallback: covers append techniques whose first mutations
            // slip past us (odd innerHTML timing). Stops after ~30s — the
            // observer keeps running.
            let polls = 0;
            const poll = setInterval(() => {
                polls++;
                scanAndInject();
                if (polls > 60) clearInterval(poll);
            }, 500);

            window._hlInjectorDisconnect = () => {
                observer.disconnect();
                clearInterval(poll);
                if (mql.removeEventListener) mql.removeEventListener('change', onBreakpointChange);
                else mql.removeListener(onBreakpointChange);
            };
        } // end start()
    }

    // ── DOM builders ────────────────────────────────────────────────────────────

    function buildHighlightRow(hl, resolveImageUrl) {
        const isLarge = hl.grid_span === 'Large';

        // One highlight = one row-cell. grid_span controls the block's WIDTH
        // via the theme's fraction classes (Large = 2/3 desktop, Small = 1/3;
        // both 1/2 on phone).
        //
        // The wrapper deliberately does NOT carry the `product-item` class:
        // theme scripts select `.product-item` (layout switcher, swatches,
        // resize observers) and would process highlight blocks as product
        // cards. The card styles .product-item provided are duplicated on
        // .hl-row in injectStyles() instead. data-hl-class + the self-heal
        // in scanAndInject() remain as a second line of defense.
        const wrapper = document.createElement('div');
        wrapper.className  = 'hl-row' + (isLarge ? ' 2/3--desk 2/3--tablet-and-up 1/2--phone' : ' 1/3--desk 1/3--tablet-and-up 1/2--phone');
        wrapper.dataset.hl = '1'; // stamp so MutationObserver ignores it
        wrapper.setAttribute('data-hl-class', wrapper.className); // for self-healing

        wrapper.appendChild(buildBlock(hl, isLarge, resolveImageUrl));

        return wrapper;
    }

    // Mobile/desktop swap happens at this max-width (matches the CSS @media).
    const MOBILE_MEDIA = '(max-width: 640px)';

    function buildBlock(entry, isLarge, resolveImageUrl) {
        const resolve  = (v) => (resolveImageUrl ? resolveImageUrl(v) : v);
        const desktop  = resolve(entry.image);
        // Use the mobile image only if it's a non-empty value; otherwise fall
        // back to the desktop image. Catches null, undefined, "" and "  ".
        const hasMobile = entry.image_mobile != null
            && String(entry.image_mobile).trim() !== '';
        const mobile   = hasMobile ? resolve(entry.image_mobile) : desktop;

        const block = document.createElement('div');
        block.className = 'hl-block' + (isLarge ? ' hl-block--large' : '');

        // <picture>: the browser loads the mobile source under the breakpoint
        // and the desktop one above it. Only one request is made.
        block.innerHTML = `
      <div class="hl-block__image">
        <picture>
          <source media="${MOBILE_MEDIA}" srcset="${escapeAttr(mobile)}">
          <img src="${escapeAttr(desktop)}" alt="" loading="lazy" />
        </picture>
      </div>
     
      <div class="hl-block__content">
       <a class="hl-block__cta" href="${escapeAttr(localizeLink(entry.link))}">
        <div class="hl-block__title">${entry.text}</div>
        </a>
      </div>`;

        return block;
    }

    // ── Styles ───────────────────────────────────────────────────────────────────

    function injectStyles() {
        if (document.getElementById('hl-injector-styles')) return;
        const style = document.createElement('style');
        style.id = 'hl-injector-styles';
        style.textContent = `
      /* Card behavior copied from the theme's .product-item so the wrapper
         doesn't need that class (theme scripts select .product-item and
         would process highlight blocks as product cards). Width still comes
         from the theme's fraction classes (2/3--desk etc.). */
      .hl-row {
        background: transparent;
        white-space: normal;
        flex-shrink: 0;
        min-width: 0;
        padding: 5px;
        position: relative;
        animation: hlFadeIn 0.4s ease;
      }
      .hl-row:after {
        content: "";
        z-index: 2;
        pointer-events: none;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
      }

      @keyframes hlFadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to   { opacity: 1; transform: translateY(0); }
      }

      .hl-block {
        display: flex;
        align-items: stretch;
        min-height: 150px;
        border-radius: 10px;
        overflow: hidden;
        border: 1px solid #c9c5b6;
        background: #f7f5f0;
        position: relative;
        height: 100%;
      }

      .hl-block__image { width: 100%; height: 100%; flex-shrink: 0; overflow: hidden; position: absolute;}
      .hl-block--large .hl-block__image { width: 100%; }
      .hl-block__image picture { display: block; width: 100%; height: 100%; }
      .hl-block__image img { width: 100%; height: 100%; object-fit: cover; display: block; }

      .hl-block__content {
        padding: 1.25rem 1.5rem;
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 8px;
        align-items: center;
        width: 100%;
        z-index: 1;
        min-height: 320px;
      }

      .hl-block__badge {
        display: inline-block;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        padding: 3px 10px;
        border-radius: 20px;
        background: #fde68a;
        color: #92400e;
        width: fit-content;
      }

      .hl-block__title {
        font-size: 2rem;
        margin: 0;
        line-height: 2rem;
        font-family: var(--heading-font-family) !important;
        color: #fff;
      }

      .hl-block__cta {
        font-size: 14px;
        color: #1a6fa8;
        text-decoration: none;
        font-weight: 500;
        width: 100%;
        height: 100%;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .hl-block__cta:hover { text-decoration: none; }
    `;
        document.head.appendChild(style);
    }

    // ── Helpers ──────────────────────────────────────────────────────────────────

    function escapeAttr(str) {
        return String(str).replace(/"/g, '&quot;');
    }

    /**
     * Prefix root-relative links with the active locale, e.g. /abc -> /en/abc.
     * - Absolute URLs, protocol-relative, mailto/tel, and #anchors: unchanged.
     * - Non-relative or empty values: returned as-is (falls back to '#').
     * - Already-localized links (/en/abc when locale is /en/): unchanged.
     * Uses Shopify.routes.root, which is '/' on the default locale and
     * '/<locale>/' otherwise (always has a trailing slash).
     */
    function localizeLink(link) {
        const href = (link == null ? '' : String(link)).trim();
        if (!href) return '#';

        // Leave anything that isn't a plain root-relative path alone.
        if (!href.startsWith('/') || href.startsWith('//')) return href;

        const root = (window.Shopify
            && window.Shopify.routes
            && window.Shopify.routes.root) || '/';

        if (root === '/') return href;          // default locale: no prefix

        const prefix = root.replace(/\/$/, ''); // '/en/' -> '/en'

        // Already prefixed? ('/en' exactly, or '/en/...')
        if (href === prefix || href.startsWith(prefix + '/')) return href;

        return prefix + href;                    // '/en' + '/abc' -> '/en/abc'
    }

    function stripHtml(str) {
        const tmp = document.createElement('div');
        tmp.innerHTML = String(str);
        return (tmp.textContent || '').trim();
    }

    function escapeHtml(str) {
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    window.initHighlightInjector = initHighlightInjector;
})();
